import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GestoresService } from './services/gestores.service';
import { Gestor } from './interfaces/gestores.interface';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-gestores-form',
  templateUrl: './gestores-form.component.html'
})
export class GestoresFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: Gestor | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  @ViewChild('agendaModal') agendaModal: any;

  form: FormGroup;
  loading: boolean = false;
  private destroy$ = new Subject<void>();

  // Arrays para los dropdowns
  agencias: any[] = [];
  claves: any[] = [];
  ciudades: any[] = [];
  estados: any[] = [];
  empresas: any[] = [];
  regionales: any[] = [];
  tipos: any[] = [];
  supervisores: any[] = [];
  sucursales: any[] = [];

  constructor(
    private fb: FormBuilder,
    private gestoresService: GestoresService,
    private messageService: MessageService
  ) {
    this.form = this.fb.group({
      sAgencia: [null],
      clave: [null],
      nombre_titular: [''],
      calle: [''],
      numero_int: [''],
      ciudad: [null],
      cve_estado: [null],
      rfc: [''],
      telefono2: [''],
      sEmpresa: [null],
      regional: [null],
      codigoInterno: [0],
      tipo: [null],
      supervisor: [null],
      numero_ext: [''],
      colonia: [''],
      del_muni: [''],
      cp: [0],
      telefono: [''],
      fax: [''],
      sSucursal: [null],
      diasEspera: [0]
    });
  }

  ngOnInit(): void {
    this.loadDropdowns();
    
    // Suscribirse a cambios en el departamento para cargar ciudades
    this.form.get('cve_estado')?.valueChanges.subscribe(idDepartamento => {
      if (idDepartamento) {
        this.loadCiudades(idDepartamento);
      } else {
        this.ciudades = [];
        this.form.patchValue({ ciudad: null });
      }
    });

    // Suscribirse a cambios en la empresa para cargar sucursales
    this.form.get('sEmpresa')?.valueChanges.subscribe(empresa => {
      if (empresa) {
        this.loadSucursales(empresa);
      } else {
        this.sucursales = [];
        this.form.patchValue({ sSucursal: null });
      }
    });

    if (this.model) {
      this.form.patchValue(this.model);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && changes['model'].currentValue) {
      const gestorData = changes['model'].currentValue;
      
      console.log('Datos del gestor recibidos:', gestorData);
      
      // Mapear los campos correctamente
      const formData = {
        sAgencia: gestorData.sAgencia,
        clave: gestorData.clave,
        nombre_titular: gestorData.nombre_titular,
        calle: gestorData.calle,
        numero_int: gestorData.numero_int,
        ciudad: gestorData.ciudad,
        cve_estado: gestorData.cve_estado,
        rfc: gestorData.rfc,
        telefono2: gestorData.telefono2,
        sEmpresa: gestorData.sEmpresa,
        regional: gestorData.zona, // mapear zona a regional
        codigoInterno: gestorData.codigoInterno,
        tipo: gestorData.tipo,
        supervisor: gestorData.supervisor,
        numero_ext: gestorData.numero_ext,
        colonia: gestorData.colonia,
        del_muni: gestorData.del_muni,
        cp: gestorData.cp,
        telefono: gestorData.telefono,
        fax: gestorData.fax,
        sSucursal: gestorData.sSucursal, // mapear sSucursal correctamente
        diasEspera: gestorData.diasEspera
      };
      
      console.log('Datos mapeados para el formulario:', formData);
      
      this.form.patchValue(formData);
      
      // Deshabilitar el campo sAgencia en modo edición
      this.form.get('sAgencia')?.disable();
      
      // Si tenemos el departamento, cargamos las ciudades
      if (gestorData.cve_estado) {
        this.loadCiudades(gestorData.cve_estado);
      }

      // Si tenemos la empresa, cargamos las sucursales
      if (gestorData.sEmpresa) {
        this.loadSucursales(gestorData.sEmpresa);
      }
    } else if (changes['model'] && !changes['model'].currentValue) {
      // Habilitar el campo sAgencia en modo creación
      this.form.get('sAgencia')?.enable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDropdowns(): void {
    this.loading = true;
    forkJoin({
      agencias: this.gestoresService.getAgencias(),
      claves: this.gestoresService.getClaves(),
      departamentos: this.gestoresService.getDepartamentos(),
      empresas: this.gestoresService.getEmpresas(),
      regionales: this.gestoresService.getRegionales(),
      tipos: this.gestoresService.getTipos(),
      supervisores: this.gestoresService.getSupervisores()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.agencias = data.agencias.map(agencia => ({
          descripcion: `${agencia.susuario} | ${agencia.nombreusuario}`,
          valor: agencia.susuario
        }));
        this.claves = data.claves
          .filter(clave => clave.valor && clave.descripcion)
          .map(clave => ({
            descripcion: clave.descripcion,
            valor: clave.valor
          }));
        this.estados = data.departamentos.map(depto => ({
          descripcion: depto.nombre,
          valor: depto.id
        }));
        this.empresas = data.empresas.map(empresa => ({
          descripcion: `${empresa.sEmpresa} | ${empresa.sNombre} | ${empresa.cuentaContable} | ${empresa.responsable} | ${empresa.telefono}`,
          valor: empresa.sEmpresa
        }));
        this.regionales = data.regionales.map(regional => ({
          descripcion: regional.descripcion,
          valor: regional.valor
        }));
        this.tipos = data.tipos
          .filter(tipo => tipo.valor && tipo.descripcion)
          .map(tipo => ({
            descripcion: tipo.descripcion,
            valor: tipo.valor
          }));
        this.supervisores = data.supervisores.map(supervisor => ({
          descripcion: `${supervisor.sAgencia} | ${supervisor.sNombre}`,
          valor: supervisor.sAgencia
        }));
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los datos'
        });
        this.loading = false;
      }
    });
  }

  loadCiudades(idDepartamento: string): void {
    this.loading = true;
    this.gestoresService.getCiudades(idDepartamento).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (ciudades) => {
        this.ciudades = ciudades.map(ciudad => ({
          descripcion: ciudad.nombre,
          valor: ciudad.id
        }));
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las ciudades'
        });
        this.loading = false;
      }
    });
  }

  loadSucursales(empresa: string): void {
    this.loading = true;
    this.gestoresService.getSucursales(empresa).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (sucursales) => {
        this.sucursales = sucursales.map(sucursal => ({
          descripcion: `${sucursal.sSucursal} | ${sucursal.nombre_Sucursal}`,
          valor: sucursal.sSucursal
        }));
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las sucursales'
        });
        this.loading = false;
      }
    });
  }

  openAgenda(): void {
    if (this.agendaModal) {
      this.agendaModal.open();
    }
  }

  create(): void {
    if (this.form.valid) {
      this.loading = true;
      const formData = this.form.getRawValue();
      
      // Obtener el nombre de la agencia seleccionada
      const agenciaSeleccionada = this.agencias.find(ag => ag.valor === formData.sAgencia);
      const nombreAgencia = agenciaSeleccionada ? agenciaSeleccionada.descripcion : '';
      
      const gestorData = {
        ...formData,
        sNombre: nombreAgencia
      };
      
      this.gestoresService.createGestor(gestorData).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({
              success: true,
              message: 'Gestor creado correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al crear el gestor'
            });
          }
        },
        error: (error) => {
          console.error('Error al crear gestor:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear el gestor'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
    }
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    // Obtener el nombre de la agencia seleccionada
    const agenciaSeleccionada = this.agencias.find(ag => ag.valor === formData.sAgencia);
    const nombreAgencia = agenciaSeleccionada ? agenciaSeleccionada.descripcion : '';
    
    const gestorData = {
      ...formData,
      sNombre: nombreAgencia
    };
    
    this.gestoresService.updateGestor(gestorData).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({
            success: true,
            message: 'Gestor actualizado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el gestor'
          });
        }
      },
      error: (error) => {
        console.error('Error al actualizar gestor:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el gestor'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }
} 